import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogActions,
    DialogTitle,
    Button,
} from "@mui/material";
import { GridColDef, GridActionsCellItem, GridCellParams, GridRenderCellParams, GridRenderEditCellParams } from "@mui/x-data-grid-premium";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import { t } from "i18next";

import { MainLayout } from "../../layout/mainLayout";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { MuiEditableDataGrid } from "../../framework/editableDataGrid/muiEditableDataGrid";

import {
    fetchVehicleMaintenanceData,
    fetchVehicleOptions,
    deleteVehicleMaintenanceData,
    saveVehicleMaintenance
} from "../../../store/vehicleSlice";
import { fetchAllVehicleGroups } from "../../../store/vehicleSlice";
import { fetchGroupedCostCenterOptions } from "../../../store/costCenterSlice";
import { VehicleMaintenanceDto } from "../../../models/vehicle/vehicleMaintenance";
import { FuelType } from "../../../models/vehicle/fuelType";
import { FieldError } from "../../framework/editableDataGrid/muiEditableDataGrid";
import { showSuccessMessage } from "../../../models/store/storeActions";
import { stringOrNumberSortComparator } from "../../framework/dataGrid/sortComparator";
import { Base } from "../../../framework/base";
import { MuiDataGridDateTimePicker } from "../../framework/editableDataGrid/muiDataGridDateTimePicker";
import { MaintenanceReminderDialog } from "../maintenanceReminderView/MaintenanceReminderDialog";
import { AddAlarm, Alarm } from "@mui/icons-material";

export interface VehicleMaintenanceDetailsListItem extends VehicleMaintenanceDto {
    vehicleGroupName: string;
    costCenterName: string;
    fuelTypeName: string;
}

export const MaintenanceView = () => (
    <MainLayout>
        <MaintenanceViewContent />
    </MainLayout>
);

export const MaintenanceViewContent = () => {
    const dispatch = useAppDispatch();

    const {
        vehicles,
        vehicleMaintenances,
        allVehicleGroups,
        vehicleMaintenancesLoading
    } = useAppSelector((s) => s.vehicle);

    const [reminderDialogOpen, setReminderDialogOpen] = useState<boolean>(false);
    const [editId, setEditId] = useState<string | undefined>();
    const costCenters = useAppSelector(
        (s) => s.costCenter.groupedOptions
    );

    const [fromDate] = useState<Date>(moment().startOf("year").toDate());
    const [untilDate] = useState<Date | null>(null);

    const [rows, setRows] = useState<VehicleMaintenanceDetailsListItem[]>([]);
    const [deleteRow, setDeleteRow] = useState<VehicleMaintenanceDetailsListItem | null>(null);

    const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
    const [selectedVehicleMaintenance, setSelectedVehicleMaintenance] =
        useState<VehicleMaintenanceDetailsListItem>(null);

    /* Helper methods */
    const getVehicleGroupName = (id?: string) =>
        allVehicleGroups?.find((g) => g.id === id)?.name ?? "";

    const getCostCenterName = (id?: string) =>
        costCenters?.find((c) => c.value === id)?.label ?? "";

    const getFuelTypeName = (fuelType?: FuelType) =>
        fuelType ? t(`common.fuelTypes.fuelType.${FuelType[fuelType]}`) : "";

    const openDialog = (row: VehicleMaintenanceDetailsListItem) => {
        setSelectedVehicleMaintenance(row);
        setReminderDialogOpen(true);
    };

    /* Load data */
    useEffect(() => {
        fetchVehicleMaintenances();

        if (!vehicles?.length) {
            dispatch(fetchVehicleOptions() as any);
        }
        if (!allVehicleGroups?.length) {
            dispatch(fetchAllVehicleGroups([]) as any);
        }
        if (!costCenters?.length) {
            dispatch(fetchGroupedCostCenterOptions() as any);
        }
    }, []);

    const fetchVehicleMaintenances = () => {
        dispatch(fetchVehicleMaintenanceData({ fromDate: fromDate, untilDate: untilDate, vehicleIds: null }) as any);
    };

    /* Map row data from vehicles and state data */
    const visibleMaintenanceRowData = useMemo(() => {
        const mergedData = (vehicleMaintenances ?? []).map(m => {
            const v = vehicles?.find((x) => x.id === m.vehicleId);

            return {
                ...m,
                vehicleId: v?.id,
                registerNumber: v?.registerNumber,
                brand: v?.brand,
                number: v?.number,
                fuelTypeName: getFuelTypeName(v?.fuelType),
                vehicleGroupName: getVehicleGroupName(v?.vehicleGroupId),
                costCenterName: getCostCenterName(v?.costCenterId),
            } as VehicleMaintenanceDetailsListItem;
        });
        return mergedData;
    }, [vehicleMaintenances, vehicles]);

    useEffect(() => {
        if (!vehicleMaintenancesLoading) {
            setRows(() => [...visibleMaintenanceRowData]);
        }

    }, [visibleMaintenanceRowData, vehicleMaintenancesLoading]);

    const isCellEditable = (params: GridCellParams) => {
        const maintenanceFields = [
            "title",
            "maintenanceDate",
            "price",
            "comment",
            "driverComment"
        ];

        // Vehicle can be selected ONLY for new rows
        if (params.field === "vehicleId") {
            return params.id === "new";
        }

        return maintenanceFields.includes(params.field);
    };

    /* Column definitions */
    const columns: GridColDef<VehicleMaintenanceDetailsListItem>[] = useMemo(
        () => [
            {
                field: "vehicleId",
                headerName: t("vehicle.vehicle"),
                flex: 1,
                editable: true,
                type: "singleSelect",
                valueOptions: vehicles?.map((v) => ({
                    value: v.id,
                    label: `${v.registerNumber}`,
                }))
            },
            { field: "brand", headerName: t("vehicle.brand"), flex: 1, editable: false },
            {
                field: "title",
                headerName: t("vehicleMaintenance.title"),
                editable: true,
                flex: 1.5
            },
            {
                field: "maintenanceDate",
                headerName: t("vehicleMaintenance.maintenanceDate"),
                editable: true,
                flex: 1.5,
                renderCell: (
                    params: GridRenderCellParams<VehicleMaintenanceDto>
                ) => getDate(params.row?.maintenanceDate),
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <MuiDataGridDateTimePicker
                        {...params}
                        dateOnly={true}
                    />
                ),
                sortComparator: stringOrNumberSortComparator,
            },
            {
                field: "price",
                headerName: t("vehicleMaintenance.price"),
                editable: true,
                type: "number",
                flex: 1
            },
            {
                field: "comment",
                headerName: t("vehicleMaintenance.comment"),
                editable: true,
                flex: 2,
            },
            {
                field: "driverComment",
                headerName: t("vehicleMaintenance.driverComment"),
                editable: true,
                flex: 2,
            },
            {
                field: "actions",
                type: "actions",
                getActions: (params) => [
                    <GridActionsCellItem
                        key={params.id}
                        color={
                            params.row.vehicleMaintenanceReminder ? "success" : "info"
                        }
                        icon={
                            params.row.vehicleMaintenanceReminder ? <Alarm /> : <AddAlarm />
                        }
                        label="Delete"
                        onClick={() => openDialog(params.row)}
                    />,
                    <GridActionsCellItem
                        key={params.id}
                        color="error"
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => setDeleteRow(params.row)}
                    />,
                ],
            },
        ], [vehicles]);

    /* Grid event handlers */
    const handleAddRow = (): Partial<VehicleMaintenanceDetailsListItem> => {
        return {
            id: "new",
            vehicleId: "",
            title: "",
            maintenanceDate: null,
            price: null,
            comment: "",
            driverComment: ""
        };
    };

    const validateRow = (
        newRowData: VehicleMaintenanceDetailsListItem
    ): FieldError[] => {
        const errors: FieldError[] = [];

        if (!newRowData.vehicleId) {
            errors.push({ field: "vehicleId", error: t("validation.fieldIsRequired") });
        }

        return errors;
    };

    const handleSubmit = async(
        values: VehicleMaintenanceDto
    ): Promise<VehicleMaintenanceDto> => {
        return new Promise((resolve, reject) => {
            const isNew = values?.id === null;

            // set date to null if value is empty, otherwise format as YYYY-MM-DD
            if (values.maintenanceDate === "" || values.maintenanceDate === null) {
                values.maintenanceDate = null;
            } else if (values.maintenanceDate) {
                // If it's a Date object or datetime string, format it as YYYY-MM-DD for DateOnly
                const date = new Date(values.maintenanceDate);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                values.maintenanceDate = `${year}-${month}-${day}`;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            dispatch(saveVehicleMaintenance({ vehicleMaintenanceDto: values }) as any)
                .then((action : {payload: VehicleMaintenanceDto}) => {
                    setHighlightedRowId(action.payload?.id);
                    setTimeout(() => setHighlightedRowId(null), 2000);

                    const message = isNew ? t("vehicleMaintenance.maintenanceAddedSuccessfully") : t("vehicleMaintenance.maintenanceEditedSuccessfully");
                    dispatch(showSuccessMessage(message));
                    resolve(action.payload);
                }).catch((error) => {
                    const message = isNew ? t("vehicleMaintenance.saveFailed") : t("vehicleMaintenance.editFailed");
                    console.error("Error saving vehicle maintenance: ", error);
                    reject(new Error(message));
                });
        });
    };
    /* UI rendering */
    return (
        <>
            <MuiEditableDataGrid<Partial<VehicleMaintenanceDetailsListItem>>
                title={t("vehicleMaintenance.listTitle")}
                editId={editId}
                setEditId={setEditId}
                persistStateKey="EditableMaintenanceTableState_V3"
                columns={columns}
                rows={rows}
                setRows={setRows}
                handleAddRow={handleAddRow}
                handleSubmit={handleSubmit}
                validateRow={validateRow}
                addRowText={t("vehicleMaintenance.newMaintenance")}
                isCellEditable={isCellEditable}
                pagination
                highlightedRowId={highlightedRowId}
                disableColumnSelector={true}
            />

            <Dialog open={!!deleteRow} onClose={() => setDeleteRow(null)}>
                <DialogTitle>{t("vehicleMaintenance.confirmDelete")}</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setDeleteRow(null)}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        color="error"
                        onClick={() => {
                            dispatch(
                                deleteVehicleMaintenanceData({ vehicleMaintenanceId: deleteRow.id }) as any
                            );
                            setRows((r) => r.filter((x) => x.id !== deleteRow.id));
                            setDeleteRow(null);
                        }}
                    >
                        {t("common.remove")}
                    </Button>
                </DialogActions>
            </Dialog>

            <MaintenanceReminderDialog
                isEdit={!!selectedVehicleMaintenance?.vehicleMaintenanceReminder}
                open={reminderDialogOpen}
                maintenanceItem={selectedVehicleMaintenance}
                maintenanceReminderItem={selectedVehicleMaintenance?.vehicleMaintenanceReminder}
                onClose={() => setReminderDialogOpen(false)}
                fetchVehicleMaintenances={fetchVehicleMaintenances}
            />
        </>
    );
};

export const getDate = (date: string) => {
    if(!date) return "-";
    return Base.dayjsToDateStr(date);
};

export const getDateTime = (date: string) => {
    if(!date) return null;
    return Base.dayjsDateToDateTimeStr(date);
};
